/**
 * Kiosk Settings Store
 * Zustand store for kiosk settings state management
 * Follows Clean Architecture: UI → Store → Use Cases → Repository → API
 */

import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import {
  KioskSettingsJson,
  NO_ASSOCIATED_PARTY_ERROR,
  PRE_AUTH_INVALID_ERROR,
  StopSessionModeEnum
} from '../types/kiosk-settings-types';
import { LanguageItem } from '../types/languages-types';
import { type UploadAssetResponse, AssetApplicationType, AssetType } from '@/features/asset-management/types/asset-types';
import type { PendingLogoUpload, PendingMediaUpload, CropArea } from '@/features/device-customizer/kiosk/types/kiosk-types';
import { revokeFilePreviewUrl, fileToBase64 } from '@/features/device-customizer/core/utils/file-helpers';
import { normalizePreAuthValues, sanitizePreAuth, validatePreAuth } from '../utils/pre-auth-validation';
import { stripOperatorDefaults, stripKioskOverrides } from '../utils/kiosk-overrides';

interface KioskSettingsState {
  // ──────────────────────────────────────────────────────────────────────
  // STATE
  // ──────────────────────────────────────────────────────────────────────

  /** Current kiosk settings (being edited) */
  settings: KioskSettingsJson | null;

  /** Original kiosk settings (for change detection and reset) */
  originalSettings: KioskSettingsJson | null;

  /** Available languages from API */
  supportedLanguageOptions: LanguageItem[];

  /** Operator ID */
  operatorId: number | null;

  /** Country code */
  countryCode: string | undefined;

  /** Party ID */
  partyId: string | undefined;

  /** Parent actor ID — present when this operator is a sub-operator */
  parentId: number | null | undefined;

  /** Loading state (blocks UI on first load) */
  isLoading: boolean;

  /** Background refresh state (silently refreshes in background) */
  isRefreshing: boolean;

  /** Whether settings have been loaded at least once */
  hasInitiallyLoaded: boolean;

  /** Saving state */
  isSaving: boolean;

  /** Uploading state (for logo/idle screen uploads) */
  isUploading: boolean;

  /** Error message */
  error: string | null;

  /** Upload error message (separate from settings error) */
  uploadError: string | null;

  // ──────────────────────────────────────────────────────────────────────
  // PENDING UPLOADS (not yet saved to API)
  // ──────────────────────────────────────────────────────────────────────

  /** Pending logo upload (not yet saved to API) */
  pendingLogo: PendingLogoUpload | null;

  /** Pending 4-inch idle screen upload (not yet saved to API) */
  pendingIdleScreen4: PendingMediaUpload | null;

  /** Pending 5-inch idle screen upload (not yet saved to API) */
  pendingIdleScreen5: PendingMediaUpload | null;

  /** Idle screen visibility toggle for preview */
  showIdleScreenInPreview: boolean;

  /**
   * Pure kiosk-level overrides
   */
  kioskOnlyOverrides: Record<string, unknown>;

  /**
   * True when kioskOnlyOverrides contained values that now match the operator
   * default and were stripped — the Save button will be enabled so the user
   * can push the cleanup to the server without making any other change.
   */
  pendingCleanup: boolean;

  /**
   * Operator-level settings
   */
  operatorSettingsRaw: Record<string, unknown>;

  // ──────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ──────────────────────────────────────────────────────────────────────

  /** Fetch supported languages from API */
  fetchLanguages: () => Promise<void>;

  /** Fetch kiosk settings from API */
  fetchSettings: (operatorId: number) => Promise<void>;

  /** Initialize settings from a pre-fetched JSON string (kioskDetails.settings).
   *  @param kioskSettingsJson     kioskDetails.kioskSettings — pure kiosk-level overrides.
   *  @param operatorSettingsJson  kioskDetails.operatorSettings — operator baseline. */
  initFromJson: (settingsJson: string | null, operatorId: number, kioskSettingsJson?: string | null, operatorSettingsJson?: string | null) => void;

  /**
   * Save settings via registerKiosk endpoint (machine-level kiosk context).
   */
  saveAsKioskSettings: (machineId: string, countryCode: string, partyId: string, locationId: string, _existingSettingsJson: string | null) => Promise<boolean>;

  /** Update a single field in settings */
  updateField: (field: string, value: unknown) => void;

  /** Update nested settings object */
  updateNestedField: (parentField: string, nestedField: string, value: unknown) => void;

  /** Set pending logo upload (stores file until save) */
  setPendingLogo: (upload: PendingLogoUpload) => void;

  /** Clear pending logo upload */
  clearPendingLogo: () => void;

  /** Update logo crop area and cropped blob */
  updateLogoCrop: (croppedBlob: Blob, cropArea: CropArea) => void;

  /** Set pending 4-inch idle screen upload (stores file until save) */
  setPendingIdleScreen4: (upload: PendingMediaUpload) => void;

  /** Clear pending 4-inch idle screen upload */
  clearPendingIdleScreen4: () => void;

  /** Update 4-inch idle screen crop area and cropped blob */
  updateIdleScreen4Crop: (croppedBlob: Blob, cropArea: CropArea) => void;

  /** Set pending 5-inch idle screen upload (stores file until save) */
  setPendingIdleScreen5: (upload: PendingMediaUpload) => void;

  /** Clear pending 5-inch idle screen upload */
  clearPendingIdleScreen5: () => void;

  /** Update 5-inch idle screen crop area and cropped blob */
  updateIdleScreen5Crop: (croppedBlob: Blob, cropArea: CropArea) => void;

  /** Toggle idle screen visibility in preview */
  toggleIdleScreenPreview: (show: boolean) => void;

  /** Save settings to API */
  saveSettings: () => Promise<void>;

  /** Reset settings to original state */
  resetSettings: () => void;

  /** Clear error message */
  clearError: () => void;

  /** Clear upload error message */
  clearUploadError: () => void;

  // ──────────────────────────────────────────────────────────────────────
  // COMPUTED
  // ──────────────────────────────────────────────────────────────────────

  /** Check if settings have changed from original */
  hasChanges: () => boolean;
}

function getChangedValues(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const key of Object.keys(after)) {
    const afterVal = after[key];
    const beforeVal = before[key];

    if (!(key in before)) {
      // New key — always include
      changes[key] = afterVal;
    } else if (
      afterVal !== null &&
      typeof afterVal === 'object' &&
      !Array.isArray(afterVal) &&
      beforeVal !== null &&
      typeof beforeVal === 'object' &&
      !Array.isArray(beforeVal)
    ) {
      // Both are plain objects — recurse
      const nested = getChangedValues(
        beforeVal as Record<string, unknown>,
        afterVal as Record<string, unknown>
      );
      if (Object.keys(nested).length > 0) {
        changes[key] = nested;
      }
    } else if (Array.isArray(afterVal) && Array.isArray(beforeVal)) {
      // Arrays — full replace if contents differ
      if (JSON.stringify(afterVal) !== JSON.stringify(beforeVal)) {
        changes[key] = afterVal;
      }
    } else if (afterVal !== beforeVal) {
      changes[key] = afterVal;
    }
  }

  // Strip null / empty values, but only for newly-appearing keys (e.g. `newField: ''`
  // on first init) — a key that already existed in `before` and was explicitly changed
  // to null/empty is a genuine, intentional clear and must survive into the delta.
  for (const key of Object.keys(changes)) {
    if (key in before) continue;
    const v = changes[key];
    if (
      v === null ||
      v === undefined ||
      v === '' ||
      (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0) ||
      (Array.isArray(v) && v.length === 0)
    ) {
      delete changes[key];
    }
  }

  return changes;
}

export const useKioskSettingsStore = create<KioskSettingsState>((set, get) => ({
  // Initial state
  settings: null,
  originalSettings: null,
  supportedLanguageOptions: [],
  operatorId: null,
  countryCode: undefined,
  partyId: undefined,
  parentId: undefined,
  isLoading: false,
  isRefreshing: false,
  hasInitiallyLoaded: false,
  isSaving: false,
  isUploading: false,
  error: null,
  uploadError: null,
  pendingLogo: null,
  pendingIdleScreen4: null,
  pendingIdleScreen5: null,
  showIdleScreenInPreview: false,
  kioskOnlyOverrides: {},
  pendingCleanup: false,
  operatorSettingsRaw: {},

  // Initialize from pre-fetched JSON — for machine-level kiosk context
  initFromJson: (settingsJson: string | null, operatorId: number, kioskSettingsJson?: string | null, operatorSettingsJson?: string | null) => {
    // Reset pendingCleanup to false first so the cleanup effect always fires
    // when we compute it true again at the end (even if it was already true).
    set({ isLoading: true, error: null, pendingCleanup: false });
    try {
      const raw = settingsJson ? JSON.parse(settingsJson) : {};
      const kioskRaw = kioskSettingsJson ? JSON.parse(kioskSettingsJson) : {};
      const operatorRaw = operatorSettingsJson ? JSON.parse(operatorSettingsJson) as Record<string, unknown> : {};
      const initializedSettings: KioskSettingsJson = {
        cpoPassword: raw.cpoPassword ?? undefined,
        // connectedStatusSupport is operator-only; read from operator baseline first
        connectedStatusSupport: (operatorRaw.connectedStatusSupport as boolean | undefined) ?? (raw.connectedStatusSupport as boolean | undefined) ?? undefined,
        connected: raw.connected ?? 'OCPP',
        logo: raw.logo ?? undefined,
        isConnectedSorting: raw.isConnectedSorting ?? false,
        language: typeof raw.language === 'object' && raw.language !== null
          ? (raw.language.code ?? 'en')
          : (raw.language ?? 'en'),
        supportedLanguages: Array.isArray(raw.supportedLanguages)
          ? raw.supportedLanguages.map((l: unknown) =>
              typeof l === 'object' && l !== null ? ((l as Record<string, unknown>).code ?? l) : l
            )
          : [],
        connectionRequired: raw.connectionRequired ?? false,
        hideStopButton: raw.hideStopButton ?? false,
        tariff24HoursDisplayNonRelative: raw.tariff24HoursDisplayNonRelative ?? false,
        sessionTimeout: raw.sessionTimeout ?? undefined,
        idleScreen: {
          enabled: raw.idleScreen?.enabled ?? false,
          imageUrl: raw.idleScreen?.imageUrl ?? undefined,
          timeout: raw.idleScreen?.timeout ?? 0
        },
        connectedAction: {
          type: raw.connectedAction?.type ?? 'Disabled',
          // Inherit pattern: kiosk override → operator's merged value → -1000 (no time, checkbox unchecked)
          time: (kioskRaw.connectedAction?.time != null && kioskRaw.connectedAction?.time > 0)
            ? kioskRaw.connectedAction.time
            : (raw.connectedAction?.time != null && raw.connectedAction?.time > 0 ? raw.connectedAction.time : -1000)
        },
        acCreditBuffer: raw.acCreditBuffer ?? 1,
        dcCreditBuffer: raw.dcCreditBuffer ?? 2,
        minCostPerMinuteThreshold: raw.minCostPerMinuteThreshold ?? undefined,
        isSupportedIncrementalAuth: raw.isSupportedIncrementalAuth ?? false,
        maxTransactionFeePercentage: raw.maxTransactionFeePercentage ?? undefined,
        averageKwhCost: raw.averageKwhCost ?? undefined,
        supportRfidFlow: (kioskRaw.supportRfidFlow ?? raw.supportRfidFlow) ?? null,
        authApprovalMinRatio: (kioskRaw.authApprovalMinRatio ?? raw.authApprovalMinRatio) ?? undefined,
        stopSessionMode: (kioskRaw.stopSessionMode != null ? Number(kioskRaw.stopSessionMode) : raw.stopSessionMode != null ? Number(raw.stopSessionMode) : operatorRaw.stopSessionMode != null ? Number(operatorRaw.stopSessionMode) : undefined) as StopSessionModeEnum | undefined,
        licensePlate: {
          enabled: raw.licensePlate?.enabled ?? false,
          isMandatory: raw.licensePlate?.isMandatory ?? false,
          allowedCharacters: (raw.licensePlate?.allowedCharacters === 1 || raw.licensePlate?.allowedCharacters === 2)
            ? raw.licensePlate.allowedCharacters
            : undefined
        },
        phoneNumber: {
          enabled: (kioskRaw.phoneNumber?.enabled ?? raw.phoneNumber?.enabled) ?? false,
          isMandatory: (kioskRaw.phoneNumber?.isMandatory ?? raw.phoneNumber?.isMandatory) ?? false,
          phonePattern: kioskRaw.phoneNumber?.phonePattern ?? raw.phoneNumber?.phonePattern
        },
        preAuth: {
          enabled: (kioskRaw.preAuth?.enabled ?? raw.preAuth?.enabled) ?? false,
          values: normalizePreAuthValues(kioskRaw.preAuth?.values ?? raw.preAuth?.values)
        }
      };
      const originalSettingsCopy = JSON.parse(JSON.stringify(initializedSettings));
      const kioskOnlyOverrides = kioskSettingsJson
        ? (JSON.parse(kioskSettingsJson) as Record<string, unknown>)
        : {};
      const operatorSettingsRaw = operatorSettingsJson
        ? (JSON.parse(operatorSettingsJson) as Record<string, unknown>)
        : {};

      // Strip kiosk overrides that are redundant (same value as operator) so
      // the next save doesn't persist values the kiosk should be inheriting.
      const cleanedKioskOverrides = Object.keys(operatorSettingsRaw).length > 0
        ? stripOperatorDefaults(kioskOnlyOverrides, operatorSettingsRaw)
        : kioskOnlyOverrides;
      const pendingCleanup = JSON.stringify(cleanedKioskOverrides) !== JSON.stringify(kioskOnlyOverrides);


      set({
        operatorId,
        settings: initializedSettings,
        originalSettings: originalSettingsCopy,
        kioskOnlyOverrides: cleanedKioskOverrides,
        operatorSettingsRaw,
        pendingCleanup,
        isLoading: false,
        hasInitiallyLoaded: true,
        error: null
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize settings';
      set({ error: errorMessage, isLoading: false });
    }
  },

  // Save via registerKiosk endpoint (machine-level kiosk context)
  saveAsKioskSettings: async (machineId: string, countryCode: string, partyId: string, locationId: string, _existingSettingsJson: string | null) => {
    const state = get();
    if (!state.settings || !state.originalSettings) return false;
    // Format check only — the Default Credit ceiling is enforced by the panel's Save gating.
    if (validatePreAuth(state.settings.preAuth).hasError) {
      set({ error: PRE_AUTH_INVALID_ERROR });
      return false;
    }
    set({ isSaving: true, error: null });
    try {
      const services = servicesContainer.getServices();
      let updatedSettings = { ...state.settings };

      // Step 1: Upload pending logo (if exists)
      if (state.pendingLogo) {
        const fileToUpload = state.pendingLogo.croppedImageBlob
          ? new File([state.pendingLogo.croppedImageBlob], state.pendingLogo.file.name, { type: state.pendingLogo.file.type })
          : state.pendingLogo.file;
        const fileContent = await fileToBase64(fileToUpload);
        const uploadResult: UploadAssetResponse = await services.uploadAssetUseCase.execute({
          applicationType: AssetApplicationType.EnergyAndroidKiosk,
          assetType: AssetType.Image,
          name: 'kiosk-logo',
          description: 'Logo for kiosk display',
          fileName: fileToUpload.name,
          fileContent,
          contentType: fileToUpload.type,
          operatorId: state.operatorId ?? 0,
        });
        if (!uploadResult.isSuccess || !uploadResult.asset?.currentS3Url) {
          set({ error: 'Failed to upload logo: ' + uploadResult.errorMessage, isSaving: false });
          return false;
        }
        updatedSettings = { ...updatedSettings, logo: uploadResult.asset.currentS3Url };
      }

      // Step 2: Upload pending 4-inch idle screen (if exists)
      if (state.pendingIdleScreen4) {
        const fileToUpload = state.pendingIdleScreen4.croppedImageBlob
          ? new File([state.pendingIdleScreen4.croppedImageBlob], state.pendingIdleScreen4.file.name, { type: state.pendingIdleScreen4.file.type })
          : state.pendingIdleScreen4.file;
        const fileContent = await fileToBase64(fileToUpload);
        const isLottie = state.pendingIdleScreen4.mediaType === 'lottie';
        const uploadResult: UploadAssetResponse = await services.uploadAssetUseCase.execute({
          applicationType: AssetApplicationType.EnergyAndroidKiosk,
          assetType: isLottie ? AssetType.Lottie : AssetType.Image,
          name: 'kiosk-4inch-idle-screen',
          description: 'Idle screen for 4-inch kiosk display',
          fileName: fileToUpload.name,
          fileContent,
          contentType: fileToUpload.type,
          operatorId: state.operatorId ?? 0,
        });
        if (!uploadResult.isSuccess || !uploadResult.asset?.currentS3Url) {
          set({ error: 'Failed to upload 4-inch idle screen: ' + uploadResult.errorMessage, isSaving: false });
          return false;
        }
        updatedSettings = {
          ...updatedSettings,
          idleScreen: { ...updatedSettings.idleScreen, media4Url: uploadResult.asset.currentS3Url },
        };
      }

      // Step 3: Upload pending 5-inch idle screen (if exists)
      if (state.pendingIdleScreen5) {
        const fileToUpload = state.pendingIdleScreen5.croppedImageBlob
          ? new File([state.pendingIdleScreen5.croppedImageBlob], state.pendingIdleScreen5.file.name, { type: state.pendingIdleScreen5.file.type })
          : state.pendingIdleScreen5.file;
        const fileContent = await fileToBase64(fileToUpload);
        const isLottie = state.pendingIdleScreen5.mediaType === 'lottie';
        const uploadResult: UploadAssetResponse = await services.uploadAssetUseCase.execute({
          applicationType: AssetApplicationType.EnergyAndroidKiosk,
          assetType: isLottie ? AssetType.Lottie : AssetType.Image,
          name: 'kiosk-5inch-idle-screen',
          description: 'Idle screen for 5-inch kiosk display',
          fileName: fileToUpload.name,
          fileContent,
          contentType: fileToUpload.type,
          operatorId: state.operatorId ?? 0,
        });
        if (!uploadResult.isSuccess || !uploadResult.asset?.currentS3Url) {
          set({ error: 'Failed to upload 5-inch idle screen: ' + uploadResult.errorMessage, isSaving: false });
          return false;
        }
        updatedSettings = {
          ...updatedSettings,
          idleScreen: { ...updatedSettings.idleScreen, imageUrl: uploadResult.asset.currentS3Url },
        };
      }

      // Ensure default language is always in supported languages
      if (updatedSettings.language && !updatedSettings.supportedLanguages.includes(updatedSettings.language)) {
        updatedSettings = {
          ...updatedSettings,
          supportedLanguages: [...updatedSettings.supportedLanguages, updatedSettings.language],
        };
      }

      // Step 4: Compute session delta
      const sessionDelta = getChangedValues(
        state.originalSettings as Record<string, unknown>,
        updatedSettings as Record<string, unknown>
      );
      // Start from kiosk overrides already stripped of any values that match the operator
      // default — prevents redundant fields accumulating in kioskSettings over time.
      const baseKioskOverrides = Object.keys(state.operatorSettingsRaw).length > 0
        ? stripOperatorDefaults(state.kioskOnlyOverrides, state.operatorSettingsRaw)
        : state.kioskOnlyOverrides;
      const newKioskOverrides: Record<string, unknown> = { ...baseKioskOverrides };
      // Clean up stale connected:'' from previous saves ('' means no override, same as absent)
      if (newKioskOverrides['connected'] === '') delete newKioskOverrides['connected'];
      if (newKioskOverrides['authApprovalMinRatio'] === null) delete newKioskOverrides['authApprovalMinRatio'];
      for (const key of Object.keys(sessionDelta)) {
        const deltaVal = sessionDelta[key];
        const existingVal = newKioskOverrides[key];
        if (
          deltaVal !== null && typeof deltaVal === 'object' && !Array.isArray(deltaVal) &&
          existingVal !== null && existingVal !== undefined && typeof existingVal === 'object' && !Array.isArray(existingVal)
        ) {
          newKioskOverrides[key] = { ...(existingVal as Record<string, unknown>), ...(deltaVal as Record<string, unknown>) };
        } else {
          newKioskOverrides[key] = deltaVal;
        }
      }

      for (const key of Object.keys(state.originalSettings as Record<string, unknown>)) {
        if (key === 'supportRfidFlow') continue;
        const updated = (updatedSettings as Record<string, unknown>)[key];
        const original = (state.originalSettings as Record<string, unknown>)[key];
        if (updated === null && original !== null && original !== undefined) {
          delete newKioskOverrides[key];
        }
      }

      // Remove any field that now equals the operator default (no need to store it).
      // preAuth is always written as the full object, never a partial delta.
      const payload = stripKioskOverrides(newKioskOverrides, state.operatorSettingsRaw, updatedSettings.preAuth);

      // operator-only fields — never persist at machine level.
      delete payload['maxTransactionFeePercentage'];
      delete payload['connectedStatusSupport'];
      // connected: 'OCPP' is the system default — delete so the kiosk inherits from operator.
      // Also delete '' which was stored by older saves as a placeholder for "no override".
      if (payload['connected'] === 'OCPP' || payload['connected'] === '') delete payload['connected'];

      // connectedAction must always include type when present — getChangedValues only
      // returns changed sub-fields, so type can be missing when only time changed.
      if ('connectedAction' in payload && updatedSettings.connectedAction?.type) {
        payload['connectedAction'] = {
          type: updatedSettings.connectedAction.type,
          ...(payload['connectedAction'] as Record<string, unknown>),
        };
      }
      // Negative time means "no override — inherit from operator". Strip it so the
      // kiosk payload does not override with a sentinel value.
      if ('connectedAction' in payload && typeof payload['connectedAction'] === 'object') {
        const ca = payload['connectedAction'] as Record<string, unknown>;
        if (typeof ca['time'] === 'number' && (ca['time'] as number) < 0) {
          delete ca['time'];
        }
      }

      await services.assignKioskRepository.registerKiosk(machineId, countryCode, partyId, locationId, JSON.stringify(payload));

      // Step 5: Clean up pending uploads and update state
      get().clearPendingLogo();
      get().clearPendingIdleScreen4();
      get().clearPendingIdleScreen5();

      const originalSettingsCopy = JSON.parse(JSON.stringify(updatedSettings));
      set({
        settings: updatedSettings,
        originalSettings: originalSettingsCopy,
        kioskOnlyOverrides: payload,
        pendingCleanup: false,
        isSaving: false,
        error: null,
      });
      return true;
    } catch (err) {
      set({ error: 'Unknown error occurred', isSaving: false });
      return false;
    }
  },

  // Fetch languages from API
  fetchLanguages: async () => {
    try {
      const services = servicesContainer.getServices();
      const languages = await services.getKioskApplicationLanguagesUseCase.execute();

      console.log('[KioskSettingsStore] Languages fetched:', {
        count: languages.length,
        languages: languages.map(l => ({ code: l.languageCode, name: l.languageName }))
      });

      set({ supportedLanguageOptions: languages });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch languages';
      console.error('[KioskSettingsStore] Languages fetch error:', errorMessage);
      // Don't throw - gracefully degrade if languages fetch fails
      set({ supportedLanguageOptions: [] });
    }
  },

  // Fetch settings
  fetchSettings: async (operatorId: number) => {
    const state = get();
    const isFirstLoad = !state.hasInitiallyLoaded;

    // On first load, show blocking loader. On subsequent calls, silently refresh
    set({
      ...(isFirstLoad ? { isLoading: true } : { isRefreshing: true }),
      error: null
    });

    try {
      const services = servicesContainer.getServices();

      // Fetch supported languages in parallel with settings
      const languagesPromise = services.getKioskApplicationLanguagesUseCase.execute()
        .catch((error) => {
          console.warn('[KioskSettingsStore] Failed to fetch languages:', error);
          return [];
        });

      const result = await services.getKioskSettingsUseCase.execute(operatorId);
      const languages = await languagesPromise;

      console.log('[KioskSettingsStore] Settings fetched:', {
        operatorId: result.operatorId,
        countryCode: result.countryCode,
        settingsKeys: Object.keys(result.settings)
      });

      // Initialize all kiosk settings fields with proper defaults
      const initializedSettings = {
        // ──────────────────────────────────────────────────────────────────────
        // GENERAL
        // ──────────────────────────────────────────────────────────────────────
        cpoPassword: result.settings.cpoPassword ?? undefined,
        connectedStatusSupport: result.settings.connectedStatusSupport ?? undefined,
        connected: result.settings.connected ?? 'OCPP',
        logo: result.settings.logo ?? undefined,
        isConnectedSorting: result.settings.isConnectedSorting ?? false,

        // ──────────────────────────────────────────────────────────────────────
        // LANGUAGES (CRITICAL)
        // ──────────────────────────────────────────────────────────────────────
        language: result.settings.language || 'en', // Default to English
        supportedLanguages: Array.isArray(result.settings.supportedLanguages)
          ? result.settings.supportedLanguages
          : [],

        // ──────────────────────────────────────────────────────────────────────
        // SCREENS
        // ──────────────────────────────────────────────────────────────────────
        connectionRequired: result.settings.connectionRequired ?? false,
        hideStopButton: result.settings.hideStopButton ?? false,
        tariff24HoursDisplayNonRelative: result.settings.tariff24HoursDisplayNonRelative ?? false,
        sessionTimeout: result.settings.sessionTimeout ?? undefined,

        // Idle Screen - Nested object with defaults
        idleScreen: {
          enabled: result.settings.idleScreen?.enabled ?? false,
          imageUrl: result.settings.idleScreen?.imageUrl ?? undefined,
          timeout: result.settings.idleScreen?.timeout ?? 0
        },

        // Connected Action - Nested object with defaults
        connectedAction: {
          type: result.settings.connectedAction?.type ?? 'Disabled',
          time: result.settings.connectedAction?.time ?? 0
        },

        // ──────────────────────────────────────────────────────────────────────
        // PAYMENT
        // ──────────────────────────────────────────────────────────────────────
        acCreditBuffer: result.settings.acCreditBuffer ?? 1, // Default AC buffer: 1
        dcCreditBuffer: result.settings.dcCreditBuffer ?? 2, // Default DC buffer: 2
        minCostPerMinuteThreshold: result.settings.minCostPerMinuteThreshold ?? undefined,
        isSupportedIncrementalAuth: result.settings.isSupportedIncrementalAuth ?? false,
        maxTransactionFeePercentage: result.settings.maxTransactionFeePercentage ?? undefined,
        averageKwhCost: result.settings.averageKwhCost ?? undefined,
        authApprovalMinRatio: result.settings.authApprovalMinRatio ?? undefined,
        stopSessionMode: result.settings.stopSessionMode ?? undefined,
        supportRfidFlow: result.settings.supportRfidFlow ?? null,

        // ──────────────────────────────────────────────────────────────────────
        // LICENSE PLATE
        // ──────────────────────────────────────────────────────────────────────
        licensePlate: {
          enabled: result.settings.licensePlate?.enabled ?? false,
          isMandatory: result.settings.licensePlate?.isMandatory ?? false,
          allowedCharacters: (result.settings.licensePlate?.allowedCharacters === 1 || result.settings.licensePlate?.allowedCharacters === 2)
            ? result.settings.licensePlate.allowedCharacters
            : undefined
        },

        // ──────────────────────────────────────────────────────────────────────
        // PHONE NUMBER
        // ──────────────────────────────────────────────────────────────────────
        phoneNumber: {
          enabled: result.settings.phoneNumber?.enabled ?? false,
          isMandatory: result.settings.phoneNumber?.isMandatory ?? false,
          phonePattern: result.settings.phoneNumber?.phonePattern
        },

        // ──────────────────────────────────────────────────────────────────────
        // PRE-AUTH SELECTION — must be listed: operator save replaces the whole object
        // ──────────────────────────────────────────────────────────────────────
        preAuth: {
          enabled: result.settings.preAuth?.enabled ?? false,
          values: normalizePreAuthValues(result.settings.preAuth?.values)
        }
      };

      console.log('[KioskSettingsStore] All settings initialized:', {
        defaultLanguage: initializedSettings.language,
        supportedLanguagesCount: initializedSettings.supportedLanguages.length,
        supportedLanguages: initializedSettings.supportedLanguages,
        acCreditBuffer: initializedSettings.acCreditBuffer,
        dcCreditBuffer: initializedSettings.dcCreditBuffer,
        idleScreenEnabled: initializedSettings.idleScreen.enabled,
        connectedActionType: initializedSettings.connectedAction.type,
        licensePlateEnabled: initializedSettings.licensePlate.enabled
      });

      // Deep copy to ensure originalSettings is independent
      const originalSettingsCopy = JSON.parse(JSON.stringify(initializedSettings));

      set({
        operatorId: result.operatorId,
        countryCode: result.countryCode,
        partyId: result.partyId,
        parentId: result.parentId,
        settings: initializedSettings,
        originalSettings: originalSettingsCopy,
        supportedLanguageOptions: languages,
        isLoading: false,
        isRefreshing: false,
        hasInitiallyLoaded: true
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch settings';
      console.error('[KioskSettingsStore] Fetch error:', errorMessage);
      set({
        error: errorMessage,
        isLoading: false,
        isRefreshing: false,
        hasInitiallyLoaded: true
      });
      throw error;
    }
  },

  // Update single field
  updateField: (field: string, value: unknown) => {
    set((state) => {
      if (!state.settings) return state;

      return {
        settings: {
          ...state.settings,
          [field]: value
        }
      };
    });
  },

  // Update nested field (e.g., idleScreen.enabled)
  updateNestedField: (parentField: string, nestedField: string, value: unknown) => {
    set((state) => {
      if (!state.settings) return state;

      const parent = (state.settings as Record<string, unknown>)[parentField];
      if (!parent || typeof parent !== 'object') {
        return state;
      }

      return {
        settings: {
          ...state.settings,
          [parentField]: {
            ...(parent as object),
            [nestedField]: value
          }
        }
      };
    });
  },

  // Pending logo management
  setPendingLogo: (upload: PendingLogoUpload) => {
    set({ pendingLogo: upload });
  },

  clearPendingLogo: () => {
    const state = get();
    if (state.pendingLogo) {
      revokeFilePreviewUrl(state.pendingLogo.originalImageUrl);
      if (state.pendingLogo.previewUrl !== state.pendingLogo.originalImageUrl) {
        revokeFilePreviewUrl(state.pendingLogo.previewUrl);
      }
    }
    set({ pendingLogo: null });
  },

  updateLogoCrop: (croppedBlob: Blob, cropArea: CropArea) => {
    const state = get();
    if (!state.pendingLogo) return;

    // Create new preview URL for cropped image
    const previewUrl = URL.createObjectURL(croppedBlob);

    // Revoke old preview URL if it was cropped before
    if (state.pendingLogo.previewUrl !== state.pendingLogo.originalImageUrl) {
      revokeFilePreviewUrl(state.pendingLogo.previewUrl);
    }

    set({
      pendingLogo: {
        ...state.pendingLogo,
        previewUrl,
        croppedImageBlob: croppedBlob,
        cropArea,
      },
    });
  },

  // Pending 4-inch idle screen management
  setPendingIdleScreen4: (upload: PendingMediaUpload) => {
    set({ pendingIdleScreen4: upload });
  },

  clearPendingIdleScreen4: () => {
    const state = get();
    if (state.pendingIdleScreen4) {
      revokeFilePreviewUrl(state.pendingIdleScreen4.originalImageUrl);
      if (state.pendingIdleScreen4.previewUrl !== state.pendingIdleScreen4.originalImageUrl) {
        revokeFilePreviewUrl(state.pendingIdleScreen4.previewUrl);
      }
    }
    set({ pendingIdleScreen4: null });
  },

  updateIdleScreen4Crop: (croppedBlob: Blob, cropArea: CropArea) => {
    const state = get();
    if (!state.pendingIdleScreen4) return;

    // Create new preview URL for cropped image
    const previewUrl = URL.createObjectURL(croppedBlob);

    // Revoke old preview URL if it was cropped before
    if (state.pendingIdleScreen4.previewUrl !== state.pendingIdleScreen4.originalImageUrl) {
      revokeFilePreviewUrl(state.pendingIdleScreen4.previewUrl);
    }

    set({
      pendingIdleScreen4: {
        ...state.pendingIdleScreen4,
        previewUrl,
        croppedImageBlob: croppedBlob,
        cropArea,
      },
    });
  },

  // Pending 5-inch idle screen management
  setPendingIdleScreen5: (upload: PendingMediaUpload) => {
    set({ pendingIdleScreen5: upload });
  },

  clearPendingIdleScreen5: () => {
    const state = get();
    if (state.pendingIdleScreen5) {
      revokeFilePreviewUrl(state.pendingIdleScreen5.originalImageUrl);
      if (state.pendingIdleScreen5.previewUrl !== state.pendingIdleScreen5.originalImageUrl) {
        revokeFilePreviewUrl(state.pendingIdleScreen5.previewUrl);
      }
    }
    set({ pendingIdleScreen5: null });
  },

  updateIdleScreen5Crop: (croppedBlob: Blob, cropArea: CropArea) => {
    const state = get();
    if (!state.pendingIdleScreen5) return;

    // Create new preview URL for cropped image
    const previewUrl = URL.createObjectURL(croppedBlob);

    // Revoke old preview URL if it was cropped before
    if (state.pendingIdleScreen5.previewUrl !== state.pendingIdleScreen5.originalImageUrl) {
      revokeFilePreviewUrl(state.pendingIdleScreen5.previewUrl);
    }

    set({
      pendingIdleScreen5: {
        ...state.pendingIdleScreen5,
        previewUrl,
        croppedImageBlob: croppedBlob,
        cropArea,
      },
    });
  },

  // Toggle idle screen preview
  toggleIdleScreenPreview: (show: boolean) => {
    set({ showIdleScreenInPreview: show });
  },

  // Save settings
  saveSettings: async () => {
    const state = get();

    if (!state.settings || !state.originalSettings || !state.operatorId) {
      throw new Error('Settings not initialized');
    }

    if (state.parentId == null && !state.partyId) {
      set({ error: NO_ASSOCIATED_PARTY_ERROR, isSaving: false });
      return;
    }

    if (validatePreAuth(state.settings.preAuth).hasError) {
      set({ error: PRE_AUTH_INVALID_ERROR, isSaving: false });
      throw new Error(PRE_AUTH_INVALID_ERROR);
    }

    set({ isSaving: true, error: null });
    try {
      const services = servicesContainer.getServices();
      let updatedSettings = {
        ...state.settings,
        connectedAction: {
          ...state.settings.connectedAction,
          type: state.settings.connectedAction?.type ?? 'Disabled',
          time: state.settings.connectedAction?.time ?? 0,
        },
      };

      // Step 1: Upload pending logo FIRST (if exists)
      if (state.pendingLogo) {
        console.log('[KioskSettingsStore] Uploading pending logo...');

        const fileToUpload = state.pendingLogo.croppedImageBlob
          ? new File([state.pendingLogo.croppedImageBlob], state.pendingLogo.file.name, {
              type: state.pendingLogo.file.type,
            })
          : state.pendingLogo.file;

        const fileContent = await fileToBase64(fileToUpload);

        const uploadResult: UploadAssetResponse = await services.uploadAssetUseCase.execute({
          applicationType: AssetApplicationType.EnergyAndroidKiosk,
          assetType: AssetType.Image,
          name: 'kiosk-logo',
          description: 'Logo for kiosk display',
          fileName: fileToUpload.name,
          fileContent,
          contentType: fileToUpload.type,
          operatorId: state.operatorId,
        });

        if (!uploadResult.isSuccess || !uploadResult.asset?.currentS3Url) {
          set({ error: 'Failed to upload logo: ' + uploadResult.errorMessage, isSaving: false });
          return;
        }

        // Update settings with S3 URL
        updatedSettings = {
          ...updatedSettings,
          logo: uploadResult.asset.currentS3Url,
        };

        console.log('[KioskSettingsStore] Logo uploaded successfully:', uploadResult.asset.currentS3Url);
      }

      // Step 2: Upload pending 4-inch idle screen (if exists)
      if (state.pendingIdleScreen4) {
        console.log('[KioskSettingsStore] Uploading pending 4-inch idle screen...');

        const fileToUpload = state.pendingIdleScreen4.croppedImageBlob
          ? new File([state.pendingIdleScreen4.croppedImageBlob], state.pendingIdleScreen4.file.name, {
              type: state.pendingIdleScreen4.file.type,
            })
          : state.pendingIdleScreen4.file;

        const fileContent = await fileToBase64(fileToUpload);
        const isLottie = state.pendingIdleScreen4.mediaType === 'lottie';

        const uploadResult: UploadAssetResponse = await services.uploadAssetUseCase.execute({
          applicationType: AssetApplicationType.EnergyAndroidKiosk,
          assetType: isLottie ? AssetType.Lottie : AssetType.Image,
          name: 'kiosk-4inch-idle-screen',
          description: 'Idle screen for 4-inch kiosk display',
          fileName: fileToUpload.name,
          fileContent,
          contentType: fileToUpload.type,
          operatorId: state.operatorId,
        });

        if (!uploadResult.isSuccess || !uploadResult.asset?.currentS3Url) {
          set({ error: 'Failed to upload 4-inch idle screen: ' + uploadResult.errorMessage, isSaving: false });
          return;
        }

        // Update settings with S3 URL
        updatedSettings = {
          ...updatedSettings,
          idleScreen: {
            ...updatedSettings.idleScreen,
            media4Url: uploadResult.asset.currentS3Url,
          },
        };

        console.log('[KioskSettingsStore] 4-inch idle screen uploaded:', uploadResult.asset.currentS3Url);
      }

      // Step 3: Upload pending 5-inch idle screen (if exists)
      if (state.pendingIdleScreen5) {
        console.log('[KioskSettingsStore] Uploading pending 5-inch idle screen...');

        const fileToUpload = state.pendingIdleScreen5.croppedImageBlob
          ? new File([state.pendingIdleScreen5.croppedImageBlob], state.pendingIdleScreen5.file.name, {
              type: state.pendingIdleScreen5.file.type,
            })
          : state.pendingIdleScreen5.file;

        const fileContent = await fileToBase64(fileToUpload);
        const isLottie = state.pendingIdleScreen5.mediaType === 'lottie';

        const uploadResult: UploadAssetResponse = await services.uploadAssetUseCase.execute({
          applicationType: AssetApplicationType.EnergyAndroidKiosk,
          assetType: isLottie ? AssetType.Lottie : AssetType.Image,
          name: 'kiosk-5inch-idle-screen',
          description: 'Idle screen for 5-inch kiosk display',
          fileName: fileToUpload.name,
          fileContent,
          contentType: fileToUpload.type,
          operatorId: state.operatorId,
        });

        if (!uploadResult.isSuccess || !uploadResult.asset?.currentS3Url) {
          set({ error: 'Failed to upload 5-inch idle screen: ' + uploadResult.errorMessage, isSaving: false });
          return;
        }

        // Update settings with S3 URL
        updatedSettings = {
          ...updatedSettings,
          idleScreen: {
            ...updatedSettings.idleScreen,
            imageUrl: uploadResult.asset.currentS3Url,
          },
        };

        console.log('[KioskSettingsStore] 5-inch idle screen uploaded:', uploadResult.asset.currentS3Url);
      }

      // Ensure default language is always in supported languages
      if (updatedSettings.language && !updatedSettings.supportedLanguages.includes(updatedSettings.language)) {
        updatedSettings = {
          ...updatedSettings,
          supportedLanguages: [...updatedSettings.supportedLanguages, updatedSettings.language],
        };
      }

      // Step 4: Save updated settings to API (preAuth without empty slots; state keeps the 4-slot shape)
      await services.updateKioskSettingsUseCase.execute(
        state.operatorId,
        state.countryCode,
        state.partyId,
        state.parentId,
        { ...updatedSettings, preAuth: sanitizePreAuth(updatedSettings.preAuth) },
        state.originalSettings
      );

      console.log('[KioskSettingsStore] Settings saved successfully');

      // Step 5: Clean up pending uploads and update state
      get().clearPendingLogo();
      get().clearPendingIdleScreen4();
      get().clearPendingIdleScreen5();

      // Update original settings to match current (mark as pristine)
      const originalSettingsCopy = JSON.parse(JSON.stringify(updatedSettings));
      set({
        settings: updatedSettings,
        originalSettings: originalSettingsCopy,
        isSaving: false
      });
    } catch (error) {
      console.error('[KioskSettingsStore] Save error:', error instanceof Error ? error.message : error);
      set({
        error: 'Unknown error occurred',
        isSaving: false
      });
      throw error;
    }
  },

  // Reset to original
  resetSettings: () => {
    const state = get();
    if (state.originalSettings) {
      const settingsCopy = JSON.parse(JSON.stringify(state.originalSettings));
      set({
        settings: settingsCopy,
        error: null
      });
    }
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  clearUploadError: () => {
    set({ uploadError: null });
  },

  // Has changes
  hasChanges: () => {
    const state = get();
    if (!state.settings || !state.originalSettings) {
      return false;
    }
    const settingsChanged = JSON.stringify(state.settings) !== JSON.stringify(state.originalSettings);
    const hasPendingUploads = !!state.pendingLogo || !!state.pendingIdleScreen4 || !!state.pendingIdleScreen5;
    return settingsChanged || hasPendingUploads || state.pendingCleanup;
  }
}));
